/* ============================================================
   THE PREFLIGHT — the Station's in-theme test
   v1.0 · © 2026 Lund Studio LLC · enforces THE STATION STANDARD
   ------------------------------------------------------------
   A test that lives in the theme. Grades the page it's on
   against the brand + commerce + integrity law tables, paints
   a readable report board (white ground, dark ink), and logs a
   console table. Zero dependencies, no network, no storage.

   Run:  ?preflight=1  ·  #preflight  ·  PREFLIGHT.run()
   Page kinds: selling (default) · doc · game · schoolhouse
     — set <body data-preflight-page="game">, or pass
       PREFLIGHT.run({page:"game"}). data-deck="16" or a
       .schoolhouse flag auto-grades as schoolhouse.
   A law FAIL means the page is off-standard. WARN advises.
   SKIP means the check could not run here (never a pass).
   ============================================================ */
(function (root, factory) {
  if (typeof module === "object" && module.exports) module.exports = factory();
  else root.PREFLIGHT = factory();
})(typeof self !== "undefined" ? self : this, function () {
  "use strict";
  var DOC = typeof document !== "undefined" ? document : null;
  var WIN = typeof window !== "undefined" ? window : null;
  var jsErrors = [];
  if (WIN && WIN.addEventListener) {
    WIN.addEventListener("error", function (e) { jsErrors.push(e && e.message || "error"); });
  }

  var CANON = {
    action: "#00CE7E",
    crew: ["#00CE7E", "#EA41AA", "#FC4539", "#F8E44B", "#389EE1"],
    strap: "THE LAST STOP BEFORE DEEP SPACE",
    domain: "THESTATIONCALLS.COM",
    vow: "THIS RUN NEVER REPEATS",
    truth: /\+\s*SHIPPING/i,
    finality: /ALL\s+SALES\s+FINAL/i,
    bannedShipped: /\$\s?\d[^<\n]{0,16}\bSHIPPED\b/i,
    bannedReturns: /\b(refunds?|free\s+returns|return\s+policy|returnable)\b/i,
    price: /\$\s?\d/
  };

  /* ---- helpers -------------------------------------------------- */
  function q(sel) { try { return Array.prototype.slice.call(DOC.querySelectorAll(sel)); } catch (e) { return []; } }
  function bodyText() { try { return (DOC.body && (DOC.body.innerText || DOC.body.textContent)) || ""; } catch (e) { return ""; } }
  function cssCorpus() {
    var out = [];
    try {
      q("style").forEach(function (s) { out.push(s.textContent || ""); });
      var sheets = DOC.styleSheets || [];
      for (var i = 0; i < sheets.length; i++) {
        try { var r = sheets[i].cssRules || []; for (var j = 0; j < r.length; j++) out.push(r[j].cssText || ""); }
        catch (e) { /* cross-origin sheet — unreadable, fine */ }
      }
    } catch (e) {}
    return out.join("\n");
  }
  function luminance(rgbStr) {
    var m = /rgba?\((\d+)[,\s]+(\d+)[,\s]+(\d+)/.exec(rgbStr || "");
    if (!m) return null;
    var f = function (v) { v /= 255; return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4); };
    return 0.2126 * f(+m[1]) + 0.7152 * f(+m[2]) + 0.0722 * f(+m[3]);
  }
  function pageKind(opt) {
    if (opt) return opt;
    var b = DOC.body;
    var declared = b && b.getAttribute && b.getAttribute("data-preflight-page");
    if (declared) return declared;
    if (q('[data-deck="16"], .schoolhouse, [data-schoolhouse]').length) return "schoolhouse";
    if (q("[data-add]").length) return "selling";
    return "game";
  }

  /* ---- the law table -------------------------------------------- */
  /* level: "law" fails the page · "warn" advises. applies: page kinds. */
  var CHECKS = [
    /* BRAND */
    { id: "B1", group: "BRAND", level: "law", applies: ["selling", "doc", "game", "schoolhouse"],
      name: "Ground obeys the surface law",
      test: function () {
        var b = DOC.body, surf = b.getAttribute("data-st-surface") || "void";
        var L = luminance(WIN.getComputedStyle(b).backgroundColor);
        if (L == null) return { skip: true, note: "no computed ground" };
        if (surf === "day") return { pass: L > 0.5, note: "day surface, luminance " + L.toFixed(3) };
        return { pass: L < 0.09, note: "void luminance " + L.toFixed(3) };
      } },
    { id: "B2", group: "BRAND", level: "law", applies: ["selling", "doc"],
      name: "THE SHOUT — Anton carries the display",
      test: function () {
        var heads = q("h1,h2,.shout,[data-shout]");
        var hit = heads.some(function (h) { return /anton/i.test(WIN.getComputedStyle(h).fontFamily || ""); });
        return { pass: hit, note: heads.length + " display heads scanned" };
      } },
    { id: "B3", group: "BRAND", level: "law", applies: ["selling", "doc"],
      name: "Action ink present (grove green " + CANON.action + ")",
      test: function () {
        var css = cssCorpus();
        return { pass: new RegExp(CANON.action, "i").test(css) || /rgb\(\s*0[,\s]+206[,\s]+126/.test(css), note: "stylesheet corpus scanned" };
      } },
    { id: "B4", group: "BRAND", level: "warn", applies: ["selling"],
      name: "Full crew palette aboard",
      test: function () {
        var css = cssCorpus(), missing = CANON.crew.filter(function (h) { return !new RegExp(h, "i").test(css); });
        return { pass: missing.length === 0, note: missing.length ? "missing " + missing.join(" ") : "all five inks found" };
      } },
    { id: "B5", group: "BRAND", level: "law", applies: ["selling", "doc", "game", "schoolhouse"],
      name: "Signage — masthead and footer aboard",
      test: function () {
        var sign = q(".st-sign,[data-st-sign]").length, foot = q(".st-foot,[data-st-foot]").length;
        return { pass: sign >= 1 && foot >= 1, note: "masthead " + sign + " · footer " + foot };
      } },
    { id: "B6", group: "BRAND", level: "law", applies: ["selling", "doc", "game", "schoolhouse"],
      name: "The vow in the footer",
      test: function () {
        var t = bodyText().toUpperCase();
        var ok = t.indexOf(CANON.domain) > -1 && t.indexOf(CANON.vow) > -1;
        return { pass: ok, note: CANON.domain + " + \u201C" + CANON.vow + "\u201D" };
      } },
    { id: "B7", group: "BRAND", level: "law", applies: ["selling", "doc"],
      name: "The strapline flies once",
      test: function () {
        var n = bodyText().toUpperCase().split(CANON.strap).length - 1;
        return { pass: n >= 1, note: n + "\u00D7 \u201C" + CANON.strap + "\u201D" };
      } },
    /* COMMERCE */
    { id: "C1", group: "COMMERCE", level: "law", applies: ["selling"],
      name: "FEHU aboard and every control dressed",
      test: function () {
        if (!WIN.FEHU) return { pass: false, note: "FEHU missing" };
        var adds = q("[data-add]"), bare = adds.filter(function (el) { return !el.getAttribute("data-fehu-dressed"); });
        return { pass: bare.length === 0, note: adds.length + " controls, " + bare.length + " undressed" };
      } },
    { id: "C2", group: "COMMERCE", level: "law", applies: ["selling"],
      name: "Truth line at the button ($33 + SHIPPING)",
      test: function () {
        var subs = q(".fehu-sub").filter(function (s) { return CANON.truth.test(s.textContent || ""); });
        return { pass: subs.length >= 1, note: subs.length + " truth lines" };
      } },
    { id: "C3", group: "COMMERCE", level: "law", applies: ["selling", "doc", "game"],
      name: "Banned copy — \u201CSHIPPED\u201D is superseded",
      test: function () {
        var hit = CANON.bannedShipped.test(bodyText());
        return { pass: !hit, note: hit ? "found a $\u2026 SHIPPED price claim" : "clean" };
      } },
    { id: "C4", group: "COMMERCE", level: "law", applies: ["selling", "doc"],
      name: "ALL SALES FINAL stands · no returns language",
      test: function () {
        var t = bodyText();
        var fin = CANON.finality.test(t), ret = CANON.bannedReturns.test(t);
        return { pass: fin && !ret, note: (fin ? "finality stated" : "finality missing") + (ret ? " · returns language found" : "") };
      } },
    { id: "C5", group: "COMMERCE", level: "law", applies: ["selling"],
      name: "One launch bar, one job",
      test: function () {
        var n = q(".fehu-bar").length;
        return { pass: n === 1, note: n + " bars" };
      } },
    { id: "C6", group: "COMMERCE", level: "law", applies: ["selling"],
      name: "One primary per screen (FEHU audit)",
      test: function () {
        if (!WIN.FEHU || !WIN.FEHU.audit) return { skip: true, note: "no auditor" };
        var flagged = WIN.FEHU.audit();
        return { pass: flagged.length === 0, note: flagged.length + " crowded sections" };
      } },
    { id: "C7", group: "COMMERCE", level: "law", applies: ["schoolhouse"],
      name: "Deck 16 carries zero commerce",
      test: function () {
        var adds = q("[data-add]").length + q(".fehu").length;
        var priced = CANON.price.test(bodyText());
        return { pass: adds === 0 && !priced, note: adds + " controls · " + (priced ? "price text found" : "no prices") };
      } },
    { id: "C8", group: "COMMERCE", level: "law", applies: ["selling", "doc"],
      name: "No countdowns — the doctrine holds",
      test: function () {
        var n = q('[class*="countdown"],[id*="countdown"],[data-countdown]').length;
        return { pass: n === 0, note: n + " countdown elements" };
      } },
    { id: "C9", group: "COMMERCE", level: "law", applies: ["selling"],
      name: "No express button at the bay (airlock doctrine)",
      test: function () {
        var n = q(".shopify-payment-button").filter(function (el) {
          return el.offsetWidth || el.offsetHeight;
        }).length;
        return { pass: n === 0, note: n + " payment buttons visible on the deck" };
      } },
    /* INTEGRITY */
    { id: "I1", group: "INTEGRITY", level: "law", applies: ["selling", "doc", "game", "schoolhouse"],
      name: "Title and viewport meta present",
      test: function () {
        var t = (DOC.title || "").trim().length > 0, v = q('meta[name="viewport"]').length > 0;
        return { pass: t && v, note: (t ? "title ok" : "title empty") + " · " + (v ? "viewport ok" : "viewport missing") };
      } },
    { id: "I2", group: "INTEGRITY", level: "law", applies: ["selling", "doc", "game", "schoolhouse"],
      name: "Every stylesheet reached the ship",
      test: function () {
        var links = q('link[rel="stylesheet"]'), dead = links.filter(function (l) { return !l.sheet; });
        return { pass: dead.length === 0, note: links.length + " sheets, " + dead.length + " dead" };
      } },
    { id: "I3", group: "INTEGRITY", level: "law", applies: ["selling", "doc", "game", "schoolhouse"],
      name: "No hull breach — zero horizontal overflow",
      test: function () {
        var over = DOC.documentElement.scrollWidth - (WIN.innerWidth || DOC.documentElement.clientWidth);
        return { pass: over <= 1, note: over + "px overflow" };
      } },
    { id: "I4", group: "INTEGRITY", level: "law", applies: ["selling", "doc", "game", "schoolhouse"],
      name: "Every image alive",
      test: function () {
        var imgs = q("img"), dead = imgs.filter(function (i) { return i.complete && i.naturalWidth === 0; });
        return { pass: dead.length === 0, note: imgs.length + " images, " + dead.length + " dead" };
      } },
    { id: "I5", group: "INTEGRITY", level: "warn", applies: ["selling", "doc", "game", "schoolhouse"],
      name: "No dead-end anchors",
      test: function () {
        var n = q('a[href="#"], a[href=""]').length;
        return { pass: n === 0, note: n + " href=\"#\" anchors" };
      } },
    { id: "I6", group: "INTEGRITY", level: "warn", applies: ["selling", "doc", "game", "schoolhouse"],
      name: "Console silent since preflight loaded",
      test: function () { return { pass: jsErrors.length === 0, note: jsErrors.length ? jsErrors.slice(0, 2).join(" · ") : "0 errors" }; } }
  ];

  /* ---- run ------------------------------------------------------- */
  var results = [];
  function run(opts) {
    opts = opts || {};
    if (!DOC || !WIN) return [];
    var kind = pageKind(opts.page);
    results = CHECKS.filter(function (c) { return c.applies.indexOf(kind) > -1; }).map(function (c) {
      var r;
      try { r = c.test() || {}; } catch (e) { r = { skip: true, note: "check error: " + (e && e.message) }; }
      var status = r.skip ? "SKIP" : r.pass ? "PASS" : (c.level === "warn" ? "WARN" : "FAIL");
      return { id: c.id, group: c.group, name: c.name, level: c.level, status: status, note: r.note || "" };
    });
    if (typeof console !== "undefined" && console.table) {
      console.table(results.map(function (r) { return { law: r.id, status: r.status, check: r.name, note: r.note }; }));
    }
    if (opts.board !== false) board(kind);
    return results;
  }

  function tally() {
    var laws = results.filter(function (r) { return r.level === "law" && r.status !== "SKIP"; });
    var pass = laws.filter(function (r) { return r.status === "PASS"; }).length;
    return { pass: pass, of: laws.length, warns: results.filter(function (r) { return r.status === "WARN"; }).length };
  }

  /* ---- the board (a reading surface: white ground, dark ink) ---- */
  var INKS = { PASS: "#00A868", FAIL: "#D82F2F", WARN: "#B07A00", SKIP: "#8A93A2" };
  function board(kind) {
    var old = DOC.getElementById("st-preflight-board");
    if (old && old.parentNode) old.parentNode.removeChild(old);
    var t = tally();
    var el = DOC.createElement("div");
    el.id = "st-preflight-board";
    el.setAttribute("role", "dialog");
    el.setAttribute("aria-label", "Station preflight report");
    el.style.cssText = "position:fixed;inset:0;z-index:99999;overflow:auto;background:#FFFFFF;color:#05070C;" +
      "font:14px/1.6 ui-monospace,Menlo,Consolas,monospace;padding:28px 20px 60px;";
    var head = "<div style='max-width:760px;margin:0 auto;'>" +
      "<div style='display:flex;justify-content:space-between;align-items:baseline;gap:14px;flex-wrap:wrap;'>" +
      "<div style='font-weight:800;letter-spacing:.18em;'>STATION PREFLIGHT \u00B7 " + String(kind).toUpperCase() + " PAGE</div>" +
      "<button id='st-pf-x' style='font:inherit;border:1px solid #05070C;background:#fff;color:#05070C;border-radius:8px;padding:6px 12px;cursor:pointer;'>CLOSE \u00D7</button></div>" +
      "<div style='margin:10px 0 22px;font-size:22px;font-weight:800;letter-spacing:.04em;color:" + (t.pass === t.of ? INKS.PASS : INKS.FAIL) + ";'>" +
      t.pass + " / " + t.of + " LAWS PASS" + (t.warns ? " \u00B7 " + t.warns + " WARN" : "") + "</div>";
    var rows = "", lastGroup = "";
    results.forEach(function (r) {
      if (r.group !== lastGroup) { rows += "<div style='margin:20px 0 6px;font-weight:800;letter-spacing:.2em;color:#5B6472;'>" + r.group + "</div>"; lastGroup = r.group; }
      rows += "<div style='display:grid;grid-template-columns:52px 46px 1fr;gap:10px;padding:8px 0;border-top:1px solid #E6E9EF;'>" +
        "<span style='font-weight:800;color:" + INKS[r.status] + ";'>" + r.status + "</span>" +
        "<span style='color:#8A93A2;'>" + r.id + "</span>" +
        "<span><span style='font-weight:700;'>" + r.name + "</span><br><span style='color:#5B6472;font-size:12.5px;'>" + r.note + "</span></span></div>";
    });
    var foot = "<div style='margin-top:26px;padding-top:14px;border-top:1px solid #E6E9EF;color:#8A93A2;font-size:11px;letter-spacing:.16em;'>" +
      "THE STATION STANDARD \u00B7 PREFLIGHT V1.0 \u00B7 \u00A9 2026 LUND STUDIO LLC</div></div>";
    el.innerHTML = head + rows + foot;
    DOC.body.appendChild(el);
    var close = function () { if (el.parentNode) el.parentNode.removeChild(el); };
    var x = DOC.getElementById("st-pf-x"); if (x) x.addEventListener("click", close);
    DOC.addEventListener("keydown", function esc(ev) { if (ev.key === "Escape") { close(); DOC.removeEventListener("keydown", esc); } });
  }

  /* ---- auto-run on flag ----------------------------------------- */
  if (WIN && DOC) {
    var flagged = false;
    try { flagged = /[?&#]preflight(=1)?\b/.test(WIN.location.search + WIN.location.hash); } catch (e) {}
    if (flagged) {
      if (DOC.readyState === "complete") setTimeout(function () { run(); }, 60);
      else WIN.addEventListener("load", function () { setTimeout(function () { run(); }, 60); });
    }
  }

  return { version: "1.0", run: run, results: function () { return results; }, laws: CHECKS, canon: CANON };
});
